import {
  ModalFooter,
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TextArea,
  Switch,
  SearchablePopover,
  useModal,
} from "@racoon/ui";
import { Fragment, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { useDisabledJobTypes } from "~/shared/hooks/useDisabledJobTypes";
import { JOB_TYPE_CATEGORY_KEY, JOB_TYPE_CATEGORY_ORDER } from "~/shared/lib/translationMaps";
import { useT, Namespace, useApiErrorMessage, useDateFnsLocale } from "@racoon/i18n";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import {
  useAttachJobInstructionsMutation,
  useCreateJobMutation,
  useDetachJobInstructionsMutation,
  useUpdateJobMutation,
} from "~/shared/api/jobs/job.grpc";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import {
  PickupLocationType,
  ConnectProto,
  ErrorCode,
  jobCategoryToNumber,
  jobTypeToNumber,
  type ApiError,
} from "@racoon/api";
import {
  useListAccessibleOrganizationsQuery,
  useListServiceReceiversQuery,
} from "~/shared/api/organization/organization.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import {
  useGetPickupLocationGroupByIdQuery,
  useListPickupLocationGroupsQuery,
} from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useCurrentUser } from "~/shared/hooks/useCurrentUser";
import { pickInstructionPhotos } from "../model/adapters";
import { isFinalJobStatus, isJobCancellable } from "~/shared/lib/job-status";
import { useCancelJobs } from "../hooks/use-cancel-jobs";
import { useFreshJob } from "~/shared/hooks/use-fresh-job";
import { JobCardAuthor } from "../components/job-card-author";
import { chevronClass, dropdownErrorClass, dropdownTriggerClass } from "./order-form-styles";
import { CountStepper, FieldError, OrderFormRow, TriggerValue } from "./order-form-fields";
import { DISABLED_INPUT_CLASS } from "~/shared/ui/disabled-field";
import { useGetOrganizationSettingsQuery } from "~/shared/api/organization/organization.grpc";
import {
  fromTimestamp,
  toTimestamp,
  matchesAnySearch,
  matchesSearch,
  parseWholeNumber,
} from "@racoon/lib";
import {
  useCreateOrderPreselect,
  mapJobToCamelCase,
  getTodayWindow,
} from "../hooks/use-create-order-preselect.hook";
import { isClockTime, splitAvailabilitySchedule } from "~/shared/lib/availability-schedule";
import { PickupScheduleBlock } from "./pickup-schedule.block";
import { useContainerGroupSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";
import {
  isLegacySchedule,
  scheduleToForm,
} from "~/features/container-group-schedule/lib/schedule-form";
import { describeRules } from "~/features/container-group-schedule/lib/schedule-label";
import { scheduleCoversDate } from "~/shared/lib/container-group-schedule";
import type { PickupWindow } from "../model/pickup-window";
import {
  IcCancel,
  IcCompanies,
  IcGarage,
  IcGroupOfLocations,
  IcLocation,
  IcTask,
} from "@racoon/icons";
import { InstructionPhotosField } from "../components/instruction-photos.field";
import {
  encodeInstructionPhotos,
  revokeInstructionPhotos,
  type InstructionPhotoDraft,
} from "~/shared/lib/instruction-photos";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useGetOrganizationQuery } from "~/shared/api/organization/organization.grpc";
import { PickupLocationModal } from "~/features/pickup-location-modal/ui/pickup-location.modal";
import { formatPickupLocationToForm } from "../../pickup-locations/model/formatPickupLocationToForm";
import { MemoryRouter } from "react-router-dom";
import type { OrderPicks } from "../model/order-picks";
import { useJobTypeOptions } from "~/shared/hooks/useJobTypeOptions";
import { useMapLocations } from "~/shared/api/locations/use-map-locations";
import { LocationMap } from "~/shared/ui/location-map";
import type { Geofence } from "~/shared/lib/geofence";
import { routeGroupOptions } from "~/shared/lib/container-group-zone";
import { withItem } from "~/shared/lib/own-organization";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { contractOptionLabel } from "~/features/pickup-location-modal/model/contract-label";
import { newJobDepotId } from "../model/new-job-depot";

export type CreateOrderDialogBodyProps = {
  onClose: () => void;
  preselectedLocationId?: string;
  preselectedLocationName?: string;
  preselectedLocationTariff?: number;
  editOrder?: ConnectProto.Job | null;
  /** The create dialog's header follows the site and customer as they are picked */
  onPicksChange?: (picks: OrderPicks) => void;
};
type JobType = ConnectProto.JobType;
// One reference for "none", so the map's fence layer isn't redrawn on every render
const NO_GEOFENCES: Geofence[] = [];

/**
 * Contract tariffs are keyed by job type in either spelling ("JOB_TYPE_MSW" or "MSW"),
 * so every lookup here compares on the short form.
 */
const jobTypeTariffKey = (value: ConnectProto.JobType): string => {
  const fullName = ConnectProto.JobTypeSchema.values.find((v) => v.number === value)?.name ?? "";
  return fullName.startsWith("JOB_TYPE_") ? fullName.slice("JOB_TYPE_".length) : fullName;
};

const normalizeTariffKeys = (tariffs: Record<string, unknown>): string[] =>
  Object.keys(tariffs).map((key) =>
    key.startsWith("JOB_TYPE_") ? key.slice("JOB_TYPE_".length) : key,
  );

/** "CATEGORY_CO" → JobCategory.CO */
const categoryFromKey = (key: string | undefined): ConnectProto.JobCategory =>
  key
    ? ConnectProto.JobCategory[
        key.replace("CATEGORY_", "") as keyof typeof ConnectProto.JobCategory
      ]
    : ConnectProto.JobCategory.UNDEFINED;

/** The job's category follows its type. */
const categoryOfJobType = (type: ConnectProto.JobType | undefined): ConnectProto.JobCategory =>
  categoryFromKey(type === undefined ? undefined : JOB_TYPE_CATEGORY_KEY[type]);

// A job services the whole group at once in these categories, one bunker in the others —
// the default of «Кол-во контейнеров» and the factor the price is worked out with.
const TRANSPORTATION_TYPES = new Set<ConnectProto.JobType>([
  ConnectProto.JobType.TRANSPORTATION,
  ConnectProto.JobType.TRANSPORTATION_MPS,
]);

const WHOLE_GROUP_CATEGORIES = new Set([
  ConnectProto.JobCategory.MSW,
  ConnectProto.JobCategory.DISINFECTION,
  ConnectProto.JobCategory.LIQUID,
]);

export const CreateOrderDialogBody = ({
  onClose,
  preselectedLocationId,
  preselectedLocationName,
  preselectedLocationTariff,
  editOrder,
  onPicksChange,
}: CreateOrderDialogBodyProps) => {
  const t = useT();
  const currentUser = useCurrentUser();
  const { selectedCompanyId, selectedCompany } = useSelectedCompany();
  const isMobileOrTablet = useMediaQuery("(max-width: 1024px)");
  const [createJob, { isLoading: isCreating }] = useCreateJobMutation();
  const [attachJobInstructions] = useAttachJobInstructionsMutation();
  const [detachJobInstructions] = useDetachJobInstructionsMutation();
  const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();
  const { cancelJobs, isCancelling } = useCancelJobs();
  const { openModal, closeModal } = useModal();

  const [pickupLocationId, setPickupLocationId] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<{
    id: string;
    name: string;
    tariff?: number;
    type?: PickupLocationType;
    startTime?: string;
    endTime?: string;
    ownerOrganizationId?: bigint;
  } | null>(null);
  const [selectedJobType, setSelectedJobType] = useState<{
    value: JobType;
    label: string;
  } | null>(null);
  const [selectedContainerTypeId, setSelectedContainerTypeId] = useState<string | null>(null);
  // One group per job; the array just holds 0 or 1 id.
  const [selectedContainerGroupIds, setSelectedContainerGroupIds] = useState<string[]>([]);
  const selectedContainerGroupId = selectedContainerGroupIds[0] ?? null;
  const setSelectedContainerGroupId = useCallback(
    (id: string | null) => setSelectedContainerGroupIds(id ? [id] : []),
    [],
  );
  // The count and the price are held as typed, so a value that isn't a whole number stays on
  // screen, flagged, instead of being read as some other number
  const [countInput, setCountInput] = useState("1");
  const num = parseWholeNumber(countInput);
  const isCountInvalid = num === null || num < 1;
  // «Дублировать заявки»: off creates one job, on creates `num` copies
  const [isDuplicate, setIsDuplicate] = useState(false);
  // «Кол-во контейнеров» one job covers; prices the job, defaults from the picked group
  const [containersInput, setContainersInput] = useState("1");
  const containersCount = parseWholeNumber(containersInput);
  const isContainersInvalid = containersCount === null || containersCount < 1;
  // «Произвольная стоимость»: off keeps the price worked out from the contract
  const [isCustomCost, setIsCustomCost] = useState(false);
  // Instruction photos are held locally until the jobs exist — AttachJobInstructions
  // needs a job id, so they can only be uploaded after createJob returns. In edit
  // mode the id is known, but staging them keeps submit the single point at which
  // anything on the form takes effect.
  const [instructionPhotos, setInstructionPhotos] = useState<InstructionPhotoDraft[]>([]);
  const [removedInstructionIds, setRemovedInstructionIds] = useState<bigint[]>([]);
  const [finalCostInput, setFinalCostInput] = useState("0");
  const finalCost = parseWholeNumber(finalCostInput);
  // Every price the form fills in itself is a whole number of tenge
  const setFinalCost = useCallback((cost: number) => setFinalCostInput(String(cost)), []);
  const [comment, setComment] = useState("");
  const [hideFromAi, setHideFromAi] = useState(false);
  // null until the dispatcher or an edited job sets one: the default "today, now → 23:59" is
  // worked out when read, so a dialog left open for hours still submits a window from now.
  const [twWindow, setTwWindow] = useState<PickupWindow | null>(null);
  // Set by the user, unlike the window seeded from the job
  const windowTouched = useRef(false);

  const [isLocationPopoverOpen, setIsLocationPopoverOpen] = useState(false);
  const [isJobTypePopoverOpen, setIsJobTypePopoverOpen] = useState(false);
  const [isDepotPopoverOpen, setIsDepotPopoverOpen] = useState(false);
  // The executor picked on the card; a new job takes the day's depot and changes only here.
  const [pickedDepotId, setPickedDepotId] = useState<bigint | null>(null);
  const [depotError, setDepotError] = useState<string | null>(null);
  const [isContainerGroupPopoverOpen, setIsContainerGroupPopoverOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [containerGroupSearchQuery, setContainerGroupSearchQuery] = useState("");
  // Which concrete problem the user dismissed (see `errorSignature` below) — a plain boolean
  // flag used to leak into unrelated errors and hide them too.
  const [dismissedErrorSignature, setDismissedErrorSignature] = useState<string | null>(null);
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [createdJobsCount, setCreatedJobsCount] = useState(0);
  // Numbers of the jobs just created, in the "Z1391" form used across the order tables.
  const [createdJobNumbers, setCreatedJobNumbers] = useState<string[]>([]);
  // The division the backend picked to serve a job on a zoned group.
  const [createdJobDepot, setCreatedJobDepot] = useState<string | null>(null);
  const describeError = useApiErrorMessage();
  // The backend refused the job's route group, e.g. the day's division of the zone has none bound.
  const [routeGroupError, setRouteGroupError] = useState<string | null>(null);

  const { data: settings } = useGetOrganizationSettingsQuery({}, { skip: !selectedCompanyId });
  const { activeCompanyId, isReceiverUser } = useAuthSession();
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: activeCompanyId ?? undefined,
  });
  const isAiFeatureEnabled = settings?.aiEnabled ?? false;
  const isEditMode = !!editOrder;
  // Neither executed nor received by the active company's tree: shown, nothing to save.
  // A write fails closed: while the subtree is unknown only the company's own job saves.
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");
  const jobOwn = isEditMode
    ? isOwnForWrite(editOrder?.serviceProviderId, editOrder?.serviceReceiverId)
    : true;
  const isForeign = jobOwn === false;
  const isOwnUnknown = jobOwn === undefined;
  const isWriteBlocked = isForeign || isOwnUnknown;
  // The job's own group, site and customer may be on no list of ours (a zone job's): added
  // back for a foreign job, and for an own one until another is picked, so the filters hold.
  const jobItem = <T extends { id: bigint }>(item: T | undefined, selectedId?: string | null) =>
    isForeign || !selectedId || String(item?.id) === selectedId ? item : undefined;
  const isLoading = isCreating || isUpdating || isCancelling;

  // Types the organization turned off are not offered for a new job; an existing job keeps
  // its own even if it was turned off since.
  const disabledJobTypes = useDisabledJobTypes();
  const editOrderType = editOrder?.type;
  const isJobTypeOffered = useCallback(
    (type: ConnectProto.JobType) => type === editOrderType || !disabledJobTypes.types.has(type),
    [editOrderType, disabledJobTypes],
  );

  // The picked type drives the tariff lookup, the job_type filters on the lists below and,
  // through its category, everything that used to hang on a tab.
  const effectiveJobTypeNum = selectedJobType?.value;
  const jobCategory = categoryOfJobType(effectiveJobTypeNum);
  // Before a type is picked the lists take every category still enabled
  const listCategories = useMemo(
    () =>
      jobCategory !== ConnectProto.JobCategory.UNDEFINED
        ? [jobCategory]
        : JOB_TYPE_CATEGORY_ORDER.filter((key) => !disabledJobTypes.categories.has(key)).map(
            categoryFromKey,
          ),
    [jobCategory, disabledJobTypes],
  );

  const [selectedReceiver, setSelectedReceiver] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isReceiverPopoverOpen, setIsReceiverPopoverOpen] = useState(false);
  const [receiverSearchQuery, setReceiverSearchQuery] = useState("");

  // Route group ("маршрутная группа") — narrows the locations and container groups to one
  // pickup location group, and is stamped onto the jobs it creates.
  const [selectedRouteGroup, setSelectedRouteGroup] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isRouteGroupPopoverOpen, setIsRouteGroupPopoverOpen] = useState(false);
  const [routeGroupSearchQuery, setRouteGroupSearchQuery] = useState("");

  const { data: receiversResponse, isLoading: isLoadingReceivers } = useListServiceReceiversQuery(
    {
      organizationId: activeCompanyId ? BigInt(activeCompanyId) : undefined,
      jobCategories: listCategories,
    },
    { skip: !activeCompanyId },
  );
  const receivers = receiversResponse?.organizations || [];

  // Container groups are owned by the customer, so a picked customer is pushed down
  // as ownerOrganizationIds instead of being filtered out client-side.
  const containerGroupFilters = useMemo(
    () => ({
      ...(pickupLocationId ? { pickupLocationIds: [BigInt(pickupLocationId)] } : {}),
      ...(selectedReceiver ? { ownerOrganizationIds: [BigInt(selectedReceiver.id)] } : {}),
      ...(selectedRouteGroup
        ? {
            pickupLocationGroupIds: [BigInt(selectedRouteGroup.id)],
            // job_type only means anything alongside a group binding
            ...(effectiveJobTypeNum !== undefined ? { jobType: effectiveJobTypeNum } : {}),
          }
        : {}),
    }),
    [pickupLocationId, selectedReceiver, selectedRouteGroup, effectiveJobTypeNum],
  );

  const {
    data: containerGroupsResponse,
    currentData: currentContainerGroupsResponse,
    isFetching: isLoadingContainerGroups,
  } = useListContainerGroupsQuery(containerGroupFilters, {
    skip: !selectedCompanyId || (!selectedLocation && !selectedRouteGroup),
  });

  // A zone job's group may be missing from these lists: the job brings its own while its site is
  // picked. Not keyed on the picked group, so a pick doesn't re-run the edit preselect.
  const storedContainerGroup = editOrder?.containerGroup;
  const containerGroups = useMemo(
    () =>
      withItem(
        containerGroupsResponse?.containerGroups ?? [],
        isForeign || String(storedContainerGroup?.pickupLocationId) === pickupLocationId
          ? storedContainerGroup
          : undefined,
      ),
    [containerGroupsResponse, storedContainerGroup, isForeign, pickupLocationId],
  );
  const selectedContainerGroup = useMemo(
    () => containerGroups.find((cg) => cg.id.toString() === selectedContainerGroupId),
    [containerGroups, selectedContainerGroupId],
  );

  const routeGroupFilters = useMemo(
    () => ({
      ownerOrganizationIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      ...(selectedReceiver ? { serviceReceiverIds: [BigInt(selectedReceiver.id)] } : {}),
      ...(effectiveJobTypeNum !== undefined ? { jobType: effectiveJobTypeNum } : {}),
    }),
    [activeCompanyId, selectedReceiver, effectiveJobTypeNum],
  );

  const {
    data: routeGroupsResponse,
    isLoading: isLoadingRouteGroups,
    isFetching: isFetchingRouteGroups,
  } = useListPickupLocationGroupsQuery(routeGroupFilters, {
    skip: !activeCompanyId,
  });
  const routeGroups = routeGroupsResponse?.pickupLocationGroups || [];

  // On a zoned group the backend stamps the route group of the day's division itself and refuses
  // any other, so there is nothing to pick.
  const zoned = (selectedContainerGroup?.serviceProviders?.length ?? 0) > 0;
  // A picked container group offers the route groups it is bound to for the job type; with none
  // bound (or no group) the company's own groups do.
  const selectedGroupRouteGroups = useMemo(() => {
    const options = selectedContainerGroup
      ? routeGroupOptions(selectedContainerGroup, effectiveJobTypeNum)
      : [];
    return options.length > 0 ? options : undefined;
  }, [selectedContainerGroup, effectiveJobTypeNum]);
  const routeGroupList = useMemo(
    () =>
      selectedGroupRouteGroups ??
      routeGroups.map((group) => ({ id: group.id.toString(), name: group.name })),
    [selectedGroupRouteGroups, routeGroups],
  );

  const filteredRouteGroups = useMemo(
    () => routeGroupList.filter((group) => matchesSearch(group.name, routeGroupSearchQuery)),
    [routeGroupList, routeGroupSearchQuery],
  );

  // Changing the customer, group or type re-filters the group list; drop a selection that fell out of it.
  useEffect(() => {
    if (!selectedRouteGroup || isFetchingRouteGroups || isLoadingContainerGroups) return;
    if (zoned || !routeGroupList.some((group) => group.id === selectedRouteGroup.id)) {
      setSelectedRouteGroup(null);
    }
  }, [routeGroupList, isFetchingRouteGroups, isLoadingContainerGroups, selectedRouteGroup, zoned]);

  useEffect(() => setRouteGroupError(null), [selectedRouteGroup, selectedContainerGroupId]);

  const receiverIdsFromSelectedLocation = useMemo(() => {
    // With a customer picked the groups query is already narrowed to them, so this set
    // would collapse to that single id and lock the dropdown to the current choice.
    if (selectedReceiver || !selectedLocation || isLoadingContainerGroups) return null;
    const ids = new Set(
      containerGroups
        .map(
          (cg: any) => cg.ownerOrganizationId?.toString() ?? cg.owner_organization_id?.toString(),
        )
        .filter(Boolean),
    );
    // An empty set means "can't tell" (the location has no groups), not "no customer fits" —
    // filtering on it left the customer list permanently empty with no way out.
    return ids.size ? ids : null;
  }, [selectedLocation, containerGroups, isLoadingContainerGroups]);

  const filteredReceivers = useMemo(() => {
    return receivers.filter((rec) => {
      if (!matchesAnySearch([rec.name, rec.bin], receiverSearchQuery)) return false;
      // The current selection always stays listed, so it can still be seen and re-picked.
      if (selectedReceiver?.id === rec.id.toString()) return true;
      if (
        receiverIdsFromSelectedLocation &&
        !receiverIdsFromSelectedLocation.has(rec.id.toString())
      )
        return false;
      return true;
    });
  }, [receivers, receiverSearchQuery, receiverIdsFromSelectedLocation, selectedReceiver?.id]);

  // Auto-select receiver when a location is picked and all its container groups
  // belong to a single receiver. Skips when the user just explicitly cleared the
  // receiver, so the reset button isn't immediately undone by this effect. The
  // flag clears again once the location changes, since that's a new context.
  const receiverManuallyClearedRef = useRef(false);
  const lastLocationIdRef = useRef<string | null>(null);
  // A location the user picked themselves (or one prefilled from a location card) is the source
  // of truth: the customer filter is derived from it, so a later customer-filtered list must
  // not drop it.
  const isLocationPinnedRef = useRef(!!preselectedLocationId);
  useEffect(() => {
    if (isEditMode) return;
    const locationId = selectedLocation?.id ?? null;
    if (locationId !== lastLocationIdRef.current) {
      lastLocationIdRef.current = locationId;
      receiverManuallyClearedRef.current = false;
    }
    if (!selectedLocation || receiverManuallyClearedRef.current) return;
    if (
      !selectedReceiver &&
      !isLoadingContainerGroups &&
      receiverIdsFromSelectedLocation &&
      receiverIdsFromSelectedLocation.size === 1
    ) {
      const [onlyReceiverId] = receiverIdsFromSelectedLocation;
      const rec = receivers.find((r) => r.id.toString() === onlyReceiverId);
      if (rec) handleReceiverSelect(rec);
    }
  }, [
    isEditMode,
    selectedLocation,
    selectedReceiver,
    isLoadingContainerGroups,
    receiverIdsFromSelectedLocation,
    receivers,
  ]);

  const handleReceiverClear = () => {
    setSelectedReceiver(null);
    setIsReceiverPopoverOpen(false);
    setReceiverSearchQuery("");
    setSelectedContainerTypeId(null);
    setSelectedContainerGroupId(null);
    setFinalCost(0);
    receiverManuallyClearedRef.current = true;
  };

  const handleRouteGroupSelect = (group: { id: string; name: string }) => {
    setSelectedRouteGroup(group);
    setIsRouteGroupPopoverOpen(false);
    setRouteGroupSearchQuery("");
    // Picked from the container group's own bindings: the group stays
    if (selectedGroupRouteGroups) return;
    setSelectedContainerTypeId(null);
    setSelectedContainerGroupId(null);
    setFinalCost(0);
  };

  const handleRouteGroupClear = () => {
    setSelectedRouteGroup(null);
    setIsRouteGroupPopoverOpen(false);
    setRouteGroupSearchQuery("");
    setSelectedContainerTypeId(null);
    setSelectedContainerGroupId(null);
    setFinalCost(0);
  };

  const handleReceiverSelect = (rec: any) => {
    setSelectedReceiver({ id: rec.id.toString(), name: rec.name });
    setIsReceiverPopoverOpen(false);
    setSelectedContainerTypeId(null);
    setSelectedContainerGroupId(null);
    setFinalCost(0);
  };

  // Used by the customer dropdown: picking another customer invalidates the current location
  // (it belongs to the previous customer's list), so it's dropped explicitly here instead of
  // being silently swapped for whatever the refetched list happens to contain.
  const handleReceiverPick = (rec: any) => {
    if (selectedReceiver?.id !== rec.id.toString()) {
      setPickupLocationId("");
      setSelectedLocation(null);
      setSelectedJobType(null);
      isLocationPinnedRef.current = false;
    }
    handleReceiverSelect(rec);
  };

  const pickupLocationFilters = useMemo(() => {
    if (!activeCompanyId) return {};

    return {
      serviceProviderIds: [BigInt(activeCompanyId)],
      statuses: [ConnectProto.LocationStatus.ACTIVE],
      jobCategories: listCategories,
      ...(selectedReceiver?.id ? { serviceReceiverIds: [BigInt(selectedReceiver.id)] } : {}),
      ...(selectedRouteGroup
        ? {
            pickupLocationGroupIds: [BigInt(selectedRouteGroup.id)],
            ...(effectiveJobTypeNum !== undefined ? { jobType: effectiveJobTypeNum } : {}),
          }
        : {}),
    };
  }, [
    selectedReceiver?.id,
    activeCompanyId,
    listCategories,
    selectedRouteGroup,
    effectiveJobTypeNum,
  ]);

  const { currentData: locationsResponse, isFetching: isFetchingLocations } =
    useListPickupLocationsQuery(pickupLocationFilters, { skip: !activeCompanyId });

  // Same `currentData` reasoning as the container groups: while the customer-filtered request
  // is in flight there is no list, rather than the previous customer's list. This is what made
  // a previously selected location get picked instead of the new one.
  const isLoadingLocations = isFetchingLocations || (!!activeCompanyId && !locationsResponse);

  // The same for its site: it may be on another organization's list. Kept after another site is
  // picked, so the pick doesn't re-run the edit preselect and the stored site stays pickable.
  const storedPickupLocation = editOrder?.pickupLocation;
  const pickupLocations = useMemo(
    () => withItem(locationsResponse?.pickupLocations ?? [], storedPickupLocation),
    [locationsResponse, storedPickupLocation],
  );

  const locations = useMemo(() => {
    return pickupLocations.map((loc) => {
      let typeVal = PickupLocationType.PICKUP_LOCATION_TYPE_UNDEFINED;
      if (loc.type === ConnectProto.PickupLocationType.CONSTRUCTION) {
        typeVal = PickupLocationType.PICKUP_LOCATION_TYPE_CONSTRUCTION;
      } else if (loc.type === ConnectProto.PickupLocationType.HOUSEHOLD) {
        typeVal = PickupLocationType.PICKUP_LOCATION_TYPE_HOUSEHOLD;
      }
      return {
        id: loc.id.toString(),
        name: loc.name,
        tariff: loc.tariff ? Number(loc.tariff) : undefined,
        type: typeVal,
        availabilitySchedule: loc.availabilitySchedule,
        availability_schedule: loc.availabilitySchedule,
        ownerOrganizationId: loc.ownerOrganizationId,
      };
    });
  }, [pickupLocations]);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const fullLocation = useMemo(
    () =>
      selectedLocation
        ? pickupLocations.find((loc) => loc.id.toString() === selectedLocation.id)
        : undefined,
    [selectedLocation, pickupLocations],
  );
  const locationModalData = useMemo(
    () =>
      fullLocation && { ...formatPickupLocationToForm(fullLocation), id: Number(fullLocation.id) },
    [fullLocation],
  );

  // The map shows the picked site, else the first depot. Its column fits the xl frame only.
  const isWide = useMediaQuery("(min-width: 1280px)");
  const { data: mapLocations } = useMapLocations();
  const mapPoint = useMemo((): [number, number] | null => {
    if (!isWide) return null;
    if (fullLocation?.lat && fullLocation.lon) return [fullLocation.lat, fullLocation.lon];
    const depot = mapLocations?.locations.find(
      (loc) => loc.type === ConnectProto.LocationType.DEPOT,
    );
    return depot ? [depot.lat, depot.lon] : null;
  }, [isWide, fullLocation, mapLocations]);

  const selectedCustomer = useMemo(
    () =>
      withItem(receivers, jobItem(editOrder?.serviceReceiver, selectedReceiver?.id)).find(
        (receiver) => receiver.id.toString() === selectedReceiver?.id,
      ),
    // jobItem is rebuilt each render; isForeign, the one value it reads, is listed instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receivers, editOrder, isForeign, selectedReceiver?.id],
  );
  // A refetch has no list for a moment: the header keeps its links meanwhile instead of blinking
  const isPickLoading =
    (!!selectedLocation && !fullLocation) || (!!selectedReceiver && !selectedCustomer);
  useEffect(() => {
    if (!isPickLoading) onPicksChange?.({ location: fullLocation, customer: selectedCustomer });
  }, [isPickLoading, fullLocation, selectedCustomer, onPicksChange]);

  const jobTypes = useMemo(
    () => [
      {
        value: ConnectProto.JobType.REPLACE_BUNKER,
        label: t(Namespace.Orders, "types.REPLACE_BUNKER" as any),
      },
      {
        value: ConnectProto.JobType.ADD_BUNKER,
        label: t(Namespace.Orders, "types.ADD_BUNKER" as any),
      },
      {
        value: ConnectProto.JobType.REMOVE_BUNKER,
        label: t(Namespace.Orders, "types.REMOVE_BUNKER" as any),
      },
      {
        value: ConnectProto.JobType.RECEIVE_LOAD,
        label: t(Namespace.Enum, "JOB_TYPE_RECEIVE_LOAD"),
      },
      { value: ConnectProto.JobType.MSW, label: t(Namespace.Orders, "types.MSW" as any) },
      {
        value: ConnectProto.JobType.MSW_CLEANUP,
        label: t(Namespace.Enum, "JOB_TYPE_MSW_CLEANUP"),
      },
      {
        value: ConnectProto.JobType.MSW_MOBILE_CLEANUP,
        label: t(Namespace.Enum, "JOB_TYPE_MSW_MOBILE_CLEANUP"),
      },
      {
        value: ConnectProto.JobType.DISINFECTION,
        label: t(Namespace.Orders, "types.DISINFECTION" as any),
      },
      {
        value: ConnectProto.JobType.LIQUID_WASTE,
        label: t(Namespace.Enum, "JOB_TYPE_LIQUID_WASTE"),
      },
      // Not offered any more, but an existing job of these types still shows and saves its own
      {
        value: ConnectProto.JobType.TRANSPORTATION,
        label: t(Namespace.Enum, "JOB_TYPE_TRANSPORTATION"),
      },
      {
        value: ConnectProto.JobType.TRANSPORTATION_MPS,
        label: t(Namespace.Enum, "JOB_TYPE_TRANSPORTATION_MPS"),
      },
      // Multilift (double container) — used on the TIPPER tab, which the UI labels "Мультилифт".
      {
        value: ConnectProto.JobType.DOUBLE_CONSTRUCTION_REPLACE_BUNKER,
        label: t(Namespace.Enum, "JOB_TYPE_DOUBLE_CONSTRUCTION_REPLACE_BUNKER"),
      },
      {
        value: ConnectProto.JobType.DOUBLE_CONSTRUCTION_ADD_BUNKER,
        label: t(Namespace.Enum, "JOB_TYPE_DOUBLE_CONSTRUCTION_ADD_BUNKER"),
      },
      {
        value: ConnectProto.JobType.DOUBLE_CONSTRUCTION_REMOVE_BUNKER,
        label: t(Namespace.Enum, "JOB_TYPE_DOUBLE_CONSTRUCTION_REMOVE_BUNKER"),
      },
    ],
    [t],
  );

  const isFinalCostInvalid = finalCost === null;
  // The card's count is read-only: it edits the one job
  const showCountError = isDuplicate && !isEditMode && isCountInvalid;
  const showContainersError = isContainersInvalid;

  const mappedJob = useMemo(() => mapJobToCamelCase(editOrder), [editOrder]);

  const instructionPhotosRef = useRef(instructionPhotos);
  instructionPhotosRef.current = instructionPhotos;
  useEffect(() => () => revokeInstructionPhotos(instructionPhotosRef.current), []);

  // Staged rather than detached on the spot: closing the dialog without saving
  // must leave the job's photos as they were.
  const handleRemoveAttachedInstruction = (id: bigint) =>
    setRemovedInstructionIds((previous) => (previous.includes(id) ? previous : [...previous, id]));

  const freshJob = useFreshJob(mappedJob);

  // Instruction photos already on the job, minus any the user has staged for
  // removal. Only the file reports are read from the live query — the rest of
  // the form keeps the snapshot so a refetch can't clobber in-progress edits.
  const attachedInstructions = useMemo(
    () =>
      pickInstructionPhotos((freshJob ?? mappedJob)?.fileReports).filter(
        (photo) => !removedInstructionIds.includes(photo.id),
      ),
    [freshJob, mappedJob, removedInstructionIds],
  );

  const liveJob = freshJob ?? editOrder;
  // A final job stays editable; its price stops following the contract, its photos are refused
  const isClosed = isEditMode && !!liveJob && isFinalJobStatus(liveJob.status);
  const isOwnSiteForWrite = useIsOwnOrganizationForWrite();
  const isLocked = isWriteBlocked;
  // The final cost is editable on a custom cost or a closed job, never on another org's job
  const isFinalCostDisabled = isWriteBlocked || (!isCustomCost && !isClosed);
  const authorBlock = liveJob ? (
    <div className="mt-auto pt-4">
      <JobCardAuthor job={liveJob} />
    </div>
  ) : null;

  const { data: editReceiverById } = useGetOrganizationQuery(
    { organizationId: mappedJob?.serviceReceiverId ?? BigInt(0) },
    {
      skip: !isEditMode || !mappedJob?.serviceReceiverId,
    },
  );

  const selectedReceiverBin = [...receivers, editReceiverById].find(
    (receiver) => receiver && receiver.id.toString() === selectedReceiver?.id,
  )?.bin;
  // The job's group is only shown: picking it would re-filter the lists below
  const { data: jobRouteGroup } = useGetPickupLocationGroupByIdQuery(
    {
      id: mappedJob?.pickupLocationGroupId ?? BigInt(0),
      ownerOrganizationId: mappedJob?.serviceProviderId ?? BigInt(0),
    },
    { skip: !mappedJob?.pickupLocationGroupId },
  );
  const routeGroupName = selectedRouteGroup?.name ?? jobRouteGroup?.name;

  // The executor as stored now (the fresh job), not the list row the card opened from
  const storedExecutorId = liveJob?.serviceProviderId ?? mappedJob?.serviceProviderId;
  const jobExecutorId = pickedDepotId ?? storedExecutorId;
  // The executor changes on an open job not yet on a route, never by the customer.
  const isHandOverOpen =
    isEditMode &&
    !isReceiverUser &&
    liveJob?.status === ConnectProto.JobStatus.CREATED &&
    !liveJob?.routeId;
  const { currentData: accessibleOrgs } = useListAccessibleOrganizationsQuery(
    { resource: "job", action: "can_edit" },
    { skip: !isHandOverOpen },
  );
  // The backend checks both sides of a hand-over: a job of an executor the user doesn't reach stays put
  const canChangeDepot =
    isHandOverOpen &&
    !!accessibleOrgs?.organizations.some((org) => String(org.id) === String(storedExecutorId));
  // Any organization the user reaches (their own and its descendants), the zone's reachable ones first.
  const depotOptions = useMemo(() => {
    const accessible = accessibleOrgs?.organizations ?? [];
    const options = [
      ...(liveJob?.serviceProvider ? [liveJob.serviceProvider] : []),
      ...(selectedContainerGroup?.serviceProviders ?? []).filter((org) =>
        accessible.some((o) => String(o.id) === String(org.id)),
      ),
      ...accessible,
    ];
    return options.filter(
      (org, i) => options.findIndex((o) => String(o.id) === String(org.id)) === i,
    );
  }, [liveJob, selectedContainerGroup, accessibleOrgs]);
  // A new job goes to the zone's first division, else to the group's serving provider.
  const newDepotId =
    !isEditMode && !isReceiverUser && selectedContainerGroup
      ? newJobDepotId(selectedContainerGroup, fullLocation, selectedCompanyId)
      : undefined;
  const knownNewDepotName = [
    ...(selectedContainerGroup?.serviceProviders ?? []),
    selectedContainerGroup?.contract?.serviceProvider,
    selectedCompany,
  ].find((org) => org && String(org.id) === newDepotId)?.name;
  const { currentData: newDepotOrg } = useGetOrganizationQuery(
    { organizationId: BigInt(newDepotId ?? 0) },
    { skip: !newDepotId || !!knownNewDepotName },
  );
  // Hidden from the customer: it would lay out the provider's divisions.
  const depotName = isReceiverUser
    ? undefined
    : isEditMode
      ? (depotOptions.find((org) => String(org.id) === String(jobExecutorId))?.name ??
        liveJob?.serviceProvider?.name)
      : (knownNewDepotName ?? newDepotOrg?.name);

  const mappedJobCategoryNum = useMemo(() => {
    if (!mappedJob?.jobCategory) return ConnectProto.JobCategory.UNDEFINED;
    return typeof mappedJob.jobCategory === "number"
      ? mappedJob.jobCategory
      : jobCategoryToNumber(mappedJob.jobCategory);
  }, [mappedJob]);

  const mappedJobTypeNum = useMemo(() => {
    if (!mappedJob?.type) return ConnectProto.JobType.UNDEFINED;
    return typeof mappedJob.type === "number" ? mappedJob.type : jobTypeToNumber(mappedJob.type);
  }, [mappedJob]);

  useCreateOrderPreselect({
    isEditMode,
    mappedJob,
    mappedJobTypeNum,
    locations,
    receivers,
    editReceiverById,
    containerGroups,
    preselectedLocationId,
    preselectedLocationName,
    preselectedLocationTariff,
    jobTypes,
    setPickupLocationId,
    setSelectedLocation,
    setSelectedJobType,
    setSelectedReceiver,
    setSelectedContainerTypeId,
    setSelectedContainerGroupId,
    setComment,
    setHideFromAi,
    setTwWindow,
  });

  // Drop the selection only when it really disappeared from the list. Never while the list is
  // loading (an empty in-flight list is not "unavailable") and never for a location the user
  // picked themselves, since the list is filtered by the customer derived from that very
  // location — that combination is what silently replaced the chosen location with another one.
  useEffect(() => {
    if (isEditMode || !selectedLocation || isLoadingLocations || isLocationPinnedRef.current)
      return;
    const stillAvailable = locations.some((loc) => loc.id.toString() === selectedLocation.id);
    if (!stillAvailable) {
      setPickupLocationId("");
      setSelectedLocation(null);
    }
  }, [locations, selectedLocation, isLoadingLocations, isEditMode]);

  const filteredLocations = locations.filter((location) =>
    matchesSearch(location.name, locationSearchQuery),
  );

  const handleLocationSelect = useCallback(
    (
      location: {
        id: string;
        name: string;
        tariff: number | undefined;
        type: PickupLocationType;
        availabilitySchedule: string | undefined;
        ownerOrganizationId?: bigint;
      },
      // `pin`: auto-selected locations aren't pinned, they came from the current filtered list.
      // `resetJobType`: only a location the user picks themselves clears the job type — an
      // auto-derived one must never throw away a job type the user already chose.
      { pin = true, resetJobType = true }: { pin?: boolean; resetJobType?: boolean } = {},
    ) => {
      setPickupLocationId(location.id);
      setIsLocationPopoverOpen(false);
      setLocationSearchQuery("");
      isLocationPinnedRef.current = pin;

      if (!isEditMode) {
        if (resetJobType) setSelectedJobType(null);
        setSelectedContainerTypeId(null);
        setSelectedContainerGroupId(null);
        setFinalCost(0);
      }

      const [locationStartTime, locationEndTime] = splitAvailabilitySchedule(
        location.availabilitySchedule,
      );

      setSelectedLocation({ ...location, startTime: locationStartTime, endTime: locationEndTime });
    },
    [isEditMode, setFinalCost],
  );

  const handleLocationClear = () => {
    setPickupLocationId("");
    setSelectedLocation(null);
    setIsLocationPopoverOpen(false);
    setLocationSearchQuery("");
    locationManuallyClearedRef.current = true;
    isLocationPinnedRef.current = false;

    if (!isEditMode) {
      setSelectedJobType(null);
      setSelectedContainerTypeId(null);
      setSelectedContainerGroupId(null);
      setFinalCost(0);
    }
  };

  // Auto-select location when receiver is selected and has only one active location.
  // Skips when the user just explicitly cleared the location, so the reset button
  // isn't immediately undone by this effect re-firing on the cleared pickupLocationId.
  // The flag clears on the next receiver change, since that's a genuinely new context.
  const locationManuallyClearedRef = useRef(false);
  const lastReceiverIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode) return;
    const receiverId = selectedReceiver?.id ?? null;
    if (receiverId !== lastReceiverIdRef.current) {
      lastReceiverIdRef.current = receiverId;
      locationManuallyClearedRef.current = false;
    }
    if (!selectedReceiver || locationManuallyClearedRef.current) return;
    if (!pickupLocationId && !isLoadingLocations && locations.length === 1) {
      handleLocationSelect(locations[0], { pin: false, resetJobType: false });
    }
  }, [
    isEditMode,
    selectedReceiver,
    pickupLocationId,
    isLoadingLocations,
    locations,
    handleLocationSelect,
  ]);

  const handleJobTypeSelect = (typeOption: { value: JobType; label: string }) => {
    setIsJobTypePopoverOpen(false);
    if (selectedJobType?.value === typeOption.value) return;
    setSelectedJobType(typeOption);
    // The group stays: the type list is already narrowed to what the picked group's
    // contract prices.
  };

  const resetForm = () => {
    setPickupLocationId(preselectedLocationId || "");
    setSelectedLocation(
      preselectedLocationId && preselectedLocationName && preselectedLocationTariff !== undefined
        ? {
            id: preselectedLocationId,
            name: preselectedLocationName,
            tariff: preselectedLocationTariff,
          }
        : null,
    );
    setSelectedJobType(null);
    setSelectedReceiver(null);
    setReceiverSearchQuery("");
    setSelectedRouteGroup(null);
    setRouteGroupSearchQuery("");
    setIsRouteGroupPopoverOpen(false);
    setCountInput("1");
    setIsDuplicate(false);
    setContainersInput("1");
    setIsCustomCost(false);
    setComment("");
    setHideFromAi(false);
    setLocationSearchQuery("");
    setIsLocationPopoverOpen(false);
    setIsJobTypePopoverOpen(false);
    setIsContainerGroupPopoverOpen(false);
    setContainerGroupSearchQuery("");
    setSelectedContainerTypeId(null);
    setSelectedContainerGroupId(null);
    setTwWindow(null);
    windowTouched.current = false;
    setFinalCost(0);
    setInstructionPhotos((previous) => {
      revokeInstructionPhotos(previous);
      return [];
    });
    setRemovedInstructionIds([]);
    // Clean slate, so the auto-select effects are free to fire again.
    isLocationPinnedRef.current = !!preselectedLocationId;
    receiverManuallyClearedRef.current = false;
    locationManuallyClearedRef.current = false;
  };

  // ----- Tariff-by-jobType validation (contract is taken from already loaded containerGroups) -----
  const eligibleContainerGroups = useMemo(() => {
    return containerGroups.filter((cg) => {
      // Filter by receiver (customer)
      if (selectedReceiver) {
        const cgReceiverId =
          (cg as any).ownerOrganizationId?.toString() ??
          (cg as any).owner_organization_id?.toString();
        if (cgReceiverId && cgReceiverId !== selectedReceiver.id) return false;
      }
      if (effectiveJobTypeNum !== undefined) {
        const contract: any = (cg as any).contract;
        if (!contract) return false;
        const tariffs = contract.tariffRateByJobType ?? contract.tariff_rate_by_job_type;
        if (!tariffs) return false;
        return normalizeTariffKeys(tariffs).includes(jobTypeTariffKey(effectiveJobTypeNum));
      }
      return true;
    });
  }, [containerGroups, effectiveJobTypeNum, selectedReceiver]);

  const filteredContainerGroups = useMemo(
    () =>
      eligibleContainerGroups.filter((cg) =>
        matchesSearch(cg.containerType?.name || "", containerGroupSearchQuery),
      ),
    [eligibleContainerGroups, containerGroupSearchQuery],
  );

  // Container type / tariff / cost previews follow the first selected group.
  const syncContainerTypeToFirstGroup = useCallback(
    (groupIds: string[]) => {
      const firstGroup = groupIds.length
        ? containerGroups.find((cg) => cg.id.toString() === groupIds[0])
        : undefined;
      setSelectedContainerTypeId(firstGroup?.containerType?.id?.toString() ?? null);
    },
    [containerGroups],
  );

  // Only one group fits the location/customer/job type — nothing to choose, so pick it.
  useEffect(() => {
    // currentData, not data: right after the location/customer changes `data` still holds the
    // previous list, and its lone group must not be picked for the new context.
    if (isEditMode || selectedContainerGroupId || !currentContainerGroupsResponse) return;
    if (eligibleContainerGroups.length !== 1) return;
    const next = [eligibleContainerGroups[0].id.toString()];
    setSelectedContainerGroupIds(next);
    syncContainerTypeToFirstGroup(next);
  }, [
    isEditMode,
    selectedContainerGroupId,
    currentContainerGroupsResponse,
    eligibleContainerGroups,
    syncContainerTypeToFirstGroup,
  ]);

  const handleContainerGroupToggle = (cg: any) => {
    const next = [cg.id.toString()];
    setSelectedContainerGroupIds(next);
    syncContainerTypeToFirstGroup(next);
    setIsContainerGroupPopoverOpen(false);
    // A type the new group's contract doesn't price can't stay picked
    if (effectiveJobTypeNum !== undefined) {
      const tariffs = cg.contract?.tariffRateByJobType ?? cg.contract?.tariff_rate_by_job_type;
      if (!tariffs || !normalizeTariffKeys(tariffs).includes(jobTypeTariffKey(effectiveJobTypeNum)))
        setSelectedJobType(null);
    }
  };

  // Договор привязан к контейнерной группе. Истёкший по дате договор группу из списка не убирает —
  // заказ по ней создать всё ещё можно, она лишь помечается в выпадающем списке. Договор без даты
  // окончания считается бессрочным.
  const isContractExpired = useCallback((cg: any) => {
    const contract: any = cg?.contract;
    if (!contract) return false;
    const endAt = fromTimestamp(contract.endAt ?? contract.end_at);
    return !!endAt && endAt.getTime() < Date.now();
  }, []);

  const formatContainerGroupLabel = useCallback(
    (cg: any) =>
      `${cg.containerType?.name || ""} (${cg.numberOfContainers || 0} ${t(Namespace.Orders, "create.pieces" as any)})`,
    [t],
  );

  type ContainerGroup = (typeof containerGroups)[number];
  const selectedContainerGroups = useMemo(
    () =>
      selectedContainerGroupIds
        .map((id) => containerGroups.find((c) => c.id.toString() === id))
        .filter((cg): cg is ContainerGroup => !!cg),
    [selectedContainerGroupIds, containerGroups],
  );

  const selectedContainerGroupsLabel = useMemo(
    () => selectedContainerGroups.map(formatContainerGroupLabel).join(", "),
    [selectedContainerGroups, formatContainerGroupLabel],
  );

  const expiredSelectedContainerGroups = useMemo(
    () => selectedContainerGroups.filter((cg) => isContractExpired(cg)),
    [selectedContainerGroups, isContractExpired],
  );

  // The group's live pickup schedules (for the picked job type, once there is one), one line each.
  const dateLocale = useDateFnsLocale();
  const { schedulesByCgId: selectedCgSchedules, loaded: schedulesLoaded } =
    useContainerGroupSchedules(selectedContainerGroup ? [selectedContainerGroup] : undefined);
  const selectedSchedules = useMemo(
    () =>
      (selectedCgSchedules.get(selectedContainerGroupId ?? "") ?? []).filter(
        (s) => effectiveJobTypeNum === undefined || s.jobType === effectiveJobTypeNum,
      ),
    [selectedCgSchedules, selectedContainerGroupId, effectiveJobTypeNum],
  );
  const scheduleLines = useMemo(
    () =>
      selectedSchedules
        .map((s) =>
          isLegacySchedule(s)
            ? (s.legacyExpr ?? "")
            : describeRules(scheduleToForm(s), t, dateLocale),
        )
        .filter(Boolean),
    [selectedSchedules, t, dateLocale],
  );
  const pickupWindow = twWindow ?? getTodayWindow();
  const scheduledOnPickupDay =
    !isEditMode && selectedSchedules.some((s) => scheduleCoversDate(s, pickupWindow.from));
  // Submit waits for them, else a scheduled day would go through unconfirmed.
  const schedulesPending = !isEditMode && !!selectedContainerGroup && !schedulesLoaded;

  const expiredContractNote =
    expiredSelectedContainerGroups.length > 0 ? (
      <FieldError>
        {t(Namespace.Orders, "create.contractExpired" as any)}
        {expiredSelectedContainerGroups.length > 1 &&
          `: ${expiredSelectedContainerGroups.map(formatContainerGroupLabel).join(", ")}`}
      </FieldError>
    ) : null;

  // Contract is embedded in ContainerGroup.
  // When a group is selected — use its contract. Otherwise use first found from receiver's groups.
  const locationContract = useMemo(() => {
    if (selectedContainerGroupId) return (selectedContainerGroup as any)?.contract ?? null;
    const receiverId = selectedReceiver?.id;
    const groups = receiverId
      ? containerGroups.filter(
          (cg: any) =>
            (cg.ownerOrganizationId?.toString() ?? cg.owner_organization_id?.toString()) ===
            receiverId,
        )
      : containerGroups;
    const group = groups.find((cg: any) => cg.contract ?? cg.contract_id);
    return (group as any)?.contract ?? null;
  }, [containerGroups, selectedReceiver, selectedContainerGroupId, selectedContainerGroup]);

  // Read-only: the contract and its legal entity follow the picked group.
  const selectedGroupContract = selectedContainerGroupId ? locationContract : null;
  const contractLabel = selectedGroupContract
    ? [contractOptionLabel(selectedGroupContract, t), selectedGroupContract.serviceProvider?.name]
        .filter(Boolean)
        .join(" · ")
    : "";

  const availableJobTypeKeys = useMemo<string[]>(() => {
    if (selectedContainerGroupId) {
      // Use only the selected group's contract
      const contract: any = (selectedContainerGroup as any)?.contract;
      if (!contract) return [];
      const tariffs = contract.tariffRateByJobType ?? contract.tariff_rate_by_job_type;
      if (!tariffs) return [];
      return normalizeTariffKeys(tariffs);
    }
    // No group selected — collect from all receiver's groups so we can show what's available
    const receiverId = selectedReceiver?.id;
    const groups = receiverId
      ? containerGroups.filter(
          (cg: any) =>
            (cg.ownerOrganizationId?.toString() ?? cg.owner_organization_id?.toString()) ===
            receiverId,
        )
      : containerGroups;

    const keys = new Set<string>();
    for (const cg of groups) {
      const contract: any = (cg as any).contract;
      if (!contract) continue;
      const tariffs = contract.tariffRateByJobType ?? contract.tariff_rate_by_job_type;
      if (!tariffs) continue;
      for (const key of normalizeTariffKeys(tariffs)) keys.add(key);
    }
    return [...keys];
  }, [containerGroups, selectedReceiver, selectedContainerGroupId, selectedContainerGroup]);

  // Location selected, groups loaded, but none has a contract (or no groups at all)
  const hasNoContractOnLocation = useMemo(
    () =>
      !!selectedLocation &&
      !isLoadingContainerGroups &&
      !!containerGroupsResponse && // query already completed (not just initial state)
      !locationContract,
    [selectedLocation, isLoadingContainerGroups, containerGroupsResponse, locationContract],
  );

  const hasTariffForCurrentJobType: boolean | undefined = useMemo(() => {
    if (!selectedLocation) return undefined;
    if (effectiveJobTypeNum === undefined) return undefined;
    if (!locationContract) return undefined;
    return availableJobTypeKeys.includes(jobTypeTariffKey(effectiveJobTypeNum));
  }, [effectiveJobTypeNum, selectedLocation, locationContract, availableJobTypeKeys]);

  // Labelled and ordered by category as the contract's tariff picker lists them
  const { options: categorisedJobTypes } = useJobTypeOptions(t);
  const jobTypeRank = useMemo(
    () => new Map(categorisedJobTypes.map((option, index) => [option.type, { ...option, index }])),
    [categorisedJobTypes],
  );
  // «Тип задания» opens once a group is picked, offering the enabled types its contract prices.
  // An existing job stays within its category.
  const jobTypeOptions = useMemo(
    () =>
      jobTypes
        .filter(
          (jt) =>
            isJobTypeOffered(jt.value) &&
            !TRANSPORTATION_TYPES.has(jt.value) &&
            !!selectedContainerGroupId &&
            availableJobTypeKeys.includes(jobTypeTariffKey(jt.value)) &&
            (!isEditMode ||
              mappedJobCategoryNum === ConnectProto.JobCategory.UNDEFINED ||
              categoryOfJobType(jt.value) === mappedJobCategoryNum),
        )
        .sort(
          (a, b) =>
            (jobTypeRank.get(a.value)?.index ?? Infinity) -
            (jobTypeRank.get(b.value)?.index ?? Infinity),
        ),
    [
      jobTypes,
      jobTypeRank,
      isJobTypeOffered,
      selectedContainerGroupId,
      availableJobTypeKeys,
      isEditMode,
      mappedJobCategoryNum,
    ],
  );

  // Tariff per cube from the selected container group's contract for the current job type.
  // Only populated when a specific group is selected — no fallback to other groups.
  const getTariffForGroup = useCallback(
    (group: any): number | undefined => {
      if (!group || effectiveJobTypeNum === undefined) return undefined;
      const contract: any = group.contract;
      const tariffs = contract?.tariffRateByJobType ?? contract?.tariff_rate_by_job_type;
      if (!tariffs) return undefined;
      const fullName =
        ConnectProto.JobTypeSchema.values.find((v) => v.number === effectiveJobTypeNum)?.name ?? "";
      const value =
        tariffs[fullName] ??
        tariffs[fullName.replace("JOB_TYPE_", "")] ??
        tariffs[String(effectiveJobTypeNum)];
      return value !== undefined ? Number(value) : undefined;
    },
    [effectiveJobTypeNum],
  );

  const contractTariffForJobType = useMemo<number | undefined>(() => {
    if (!selectedContainerGroupId) return undefined;
    return getTariffForGroup(selectedContainerGroup);
  }, [selectedContainerGroup, selectedContainerGroupId, getTariffForGroup]);

  // A card opens on the job's own count (an older job saved without one starts from 1) and
  // price. Once per job: refetched lists must not undo what the dispatcher changed.
  const savedContainersCount = mappedJob?.numberOfContainers || 1;
  const savedTariff = Number(mappedJob?.jobTariff || 0);
  useEffect(() => {
    if (!isEditMode) return;
    setContainersInput(String(savedContainersCount));
    setFinalCost(savedTariff);
  }, [isEditMode, savedContainersCount, savedTariff, mappedJob?.id, setFinalCost]);

  // A new pick of group or type restarts «Кол-во контейнеров» from its default: the whole
  // group where a job services it at once, one bunker otherwise. Keyed on the default itself,
  // so a refetch of the groups doesn't wipe a count the dispatcher set.
  const selectedGroupContainers = Number(selectedContainerGroup?.numberOfContainers ?? 1);
  const defaultContainersCount = WHOLE_GROUP_CATEGORIES.has(jobCategory)
    ? selectedGroupContainers || 1
    : 1;
  useEffect(() => {
    if (!isEditMode) setContainersInput(String(defaultContainersCount));
  }, [isEditMode, selectedContainerGroupId, jobCategory, defaultContainersCount]);

  // The contract's price for what the form holds: tariff × container volume × containers
  const contractPrice = useMemo(() => {
    const containerType = selectedContainerTypeId
      ? containerGroups.find((cg) => cg.containerType?.id.toString() === selectedContainerTypeId)
          ?.containerType
      : selectedContainerGroup?.containerType;
    const tariff = contractTariffForJobType ?? selectedLocation?.tariff;
    if (!containerType || tariff === undefined) return undefined;
    return Math.round(tariff * containerType.volume * (containersCount ?? 1));
  }, [
    containerGroups,
    contractTariffForJobType,
    selectedLocation?.tariff,
    selectedContainerTypeId,
    selectedContainerGroup,
    containersCount,
  ]);

  // A card's price counts as hand-set only when it differs from the contract's, decided once
  // per job when the contract price for its saved count is known
  // State, not a ref: the price effect must wait for the render that carries the decision
  const [customCostDecidedFor, setCustomCostDecidedFor] = useState<unknown>(undefined);
  useEffect(() => {
    if (!isEditMode || !mappedJob || customCostDecidedFor === mappedJob.id) return;
    if (!mappedJob.jobTariff) {
      setIsCustomCost(false);
    } else {
      if (contractPrice === undefined || containersCount !== savedContainersCount) return;
      setIsCustomCost(Number(mappedJob.jobTariff) !== contractPrice);
    }
    setCustomCostDecidedFor(mappedJob.id);
  }, [
    isEditMode,
    mappedJob,
    customCostDecidedFor,
    contractPrice,
    containersCount,
    savedContainersCount,
  ]);

  // The price follows the contract unless set by hand; a closed job's saved price stays put
  useEffect(() => {
    if (isCustomCost || isClosed || contractPrice === undefined) return;
    if (isEditMode && customCostDecidedFor !== mappedJob?.id) return;
    setFinalCost(contractPrice);
  }, [
    contractPrice,
    isCustomCost,
    isClosed,
    isEditMode,
    customCostDecidedFor,
    mappedJob?.id,
    setFinalCost,
  ]);

  // The customer has no object for this category. Based on the full list, not
  // the searched one — an empty search result is not an error, the popover says "nothing found".
  const hasNoAvailableLocations =
    !!activeCompanyId && !isLoadingLocations && locations.length === 0;

  // Identity of the problem currently on screen: kind + the selection it belongs to. The dismiss
  // button hides exactly this signature, so picking another customer / object / job type always
  // brings the explanation back. The old boolean `isErrorDismissed` was only reset by an effect
  // watching two of the three errors, so dismissing the "no objects" banner muted the banner for
  // the rest of the dialog — that's why a field could end up outlined in red with no message.
  const errorSignature = useMemo(() => {
    const kinds = [
      hasNoContractOnLocation ? "no-contract" : "",
      hasTariffForCurrentJobType === false ? "no-tariff" : "",
      hasNoAvailableLocations ? "no-locations" : "",
    ].filter(Boolean);
    if (!kinds.length) return null;
    return [...kinds, pickupLocationId, selectedReceiver?.id ?? "", effectiveJobTypeNum ?? ""].join(
      "|",
    );
  }, [
    hasNoContractOnLocation,
    hasTariffForCurrentJobType,
    hasNoAvailableLocations,
    pickupLocationId,
    selectedReceiver?.id,
    effectiveJobTypeNum,
  ]);

  // One source of truth for the banner AND the red field outlines, so they can't disagree.
  // A foreign job has nothing to fix here, nor one not yet known as ours
  const isErrorVisible =
    !isWriteBlocked && !!errorSignature && errorSignature !== dismissedErrorSignature;
  const showNoContractError = isErrorVisible && hasNoContractOnLocation;
  const showNoTariffError = isErrorVisible && hasTariffForCurrentJobType === false;
  const showNoLocationsError = isErrorVisible && hasNoAvailableLocations;

  const submit = async () => {
    if (!selectedCompanyId || !currentUser || !pickupLocationId) return;
    // The same rule disables the submit button, so a flagged field never gets this far
    if (isFinalCostInvalid || showCountError || showContainersError) return;
    const aiVisibleValue = isAiFeatureEnabled ? !hideFromAi : false;

    // On edit the job falls back to the type it already has, so a type the picker doesn't list
    // can't block saving the rest of the form.
    const jobTypeVal =
      selectedJobType?.value ??
      (isEditMode && mappedJobTypeNum !== ConnectProto.JobType.UNDEFINED
        ? mappedJobTypeNum
        : undefined);
    // The category follows the type; a type with none keeps the job's own
    const derivedCategory = categoryOfJobType(jobTypeVal);
    const categoryVal =
      derivedCategory === ConnectProto.JobCategory.UNDEFINED && isEditMode
        ? mappedJobCategoryNum
        : derivedCategory;

    if (jobTypeVal === undefined) {
      console.error("jobTypeVal is undefined");
      return;
    }

    // A job always carries a time window; untouched, it is "today from now until 23:59".
    const { from: twFrom, to: twTo } = twWindow ?? getTodayWindow();
    const twStartAt = toTimestamp(twFrom);
    const twEndAt = toTimestamp(twTo);

    if (isEditMode && mappedJob) {
      const updateData: any = {
        id: mappedJob.id,
        pickupLocationId: BigInt(pickupLocationId),
        // UpdateJob merges what is present, so a missing id is left out rather than sent as 0
        serviceProviderId: storedExecutorId || undefined,
        serviceReceiverId: mappedJob.serviceReceiverId || undefined,
        type: jobTypeVal,
        jobTariff: BigInt(finalCost ?? 0),
        driverComment: comment,
        visibleToAi: aiVisibleValue,
        jobCategory: categoryVal,
        containerTypeId: BigInt(selectedContainerTypeId || mappedJob.containerTypeId || 0),
        numberOfContainers: containersCount ?? 1,
      };
      // A closed job moves group only when picked: the preselect may match another by container type
      const storedGroupId = String(
        mappedJob.containerGroup?.id ?? mappedJob.containerGroupId ?? "",
      );
      if (selectedContainerGroupId && (!isClosed || selectedContainerGroupId !== storedGroupId)) {
        updateData.containerGroupId = BigInt(selectedContainerGroupId);
      }
      if (selectedReceiver) {
        updateData.serviceReceiverId = BigInt(selectedReceiver.id);
      }
      // Sent only when changed: an echo of the stored executor checks nothing
      const handOver = !!pickedDepotId && String(pickedDepotId) !== String(storedExecutorId);
      if (handOver) {
        updateData.serviceProviderId = BigInt(String(pickedDepotId));
      }
      // A closed job saved without a window doesn't get the form's fallback, today
      if (!isClosed || mappedJob.twStartAt || mappedJob.twEndAt || windowTouched.current) {
        updateData.twStartAt = twStartAt;
        updateData.twEndAt = twEndAt;
      }
      let updated: ConnectProto.Job;
      try {
        updated = await updateJob(updateData).unwrap();
      } catch (error) {
        const apiError = error as ApiError;
        // A hand-over is refused while the job is routed, or when the user reaches neither side
        const depotRefused =
          apiError.code === ErrorCode.ERROR_CODE_JOB_SERVICE_PROVIDER_NOT_CHANGEABLE ||
          (handOver && apiError.code === ErrorCode.ERROR_CODE_PERMISSION_DENIED);
        if (!depotRefused) throw error;
        setDepotError(describeError(apiError));
        return;
      }
      // A group move may hand the job to another executor: the instruction calls name the saved one
      const executorId = updated.serviceProviderId || storedExecutorId;

      // Photo changes are applied after the job update, so a failing update leaves
      // the photos untouched rather than half-applied. A job closed since they were staged refuses them.
      if (!isClosed && removedInstructionIds.length > 0) {
        await detachJobInstructions({
          jobId: mappedJob.id,
          serviceProviderId: executorId,
          fileIds: removedInstructionIds,
        }).unwrap();
      }
      if (!isClosed && instructionPhotos.length > 0) {
        await attachJobInstructions({
          jobId: mappedJob.id,
          serviceProviderId: executorId,
          files: await encodeInstructionPhotos(instructionPhotos),
        }).unwrap();
      }
    } else {
      // Encoded once: every copy gets the same photos, and base64-encoding them per job would
      // repeat the expensive work on identical bytes.
      const instructionFiles =
        instructionPhotos.length > 0 ? await encodeInstructionPhotos(instructionPhotos) : [];
      const createdNumbers: string[] = [];
      let created = 0;
      let depot: string | null = null;
      const zone = selectedContainerGroup?.serviceProviders ?? [];
      const copies = isDuplicate ? (num ?? 1) : 1;

      for (let i = 0; i < copies; i++) {
        const jobData: Parameters<typeof createJob>[0] = {
          pickupLocationId: BigInt(pickupLocationId),
          type: jobTypeVal,
          jobTariff: BigInt(finalCost ?? 0),
          driverComment: comment,
          authorId: BigInt(currentUser.id),
          visibleToAi: aiVisibleValue,
          jobCategory: categoryVal,
          containerTypeId: BigInt(selectedContainerTypeId || 0),
          numberOfContainers: containersCount ?? 1,
        };
        if (selectedContainerGroupId) {
          jobData.containerGroupId = BigInt(selectedContainerGroupId);
        }
        if (selectedRouteGroup) {
          jobData.pickupLocationGroupId = BigInt(selectedRouteGroup.id);
        }
        jobData.twStartAt = twStartAt;
        jobData.twEndAt = twEndAt;
        if (isReceiverUser) {
          jobData.serviceReceiverId = BigInt(selectedCompanyId);
        } else {
          // The user's company passes the permission check; the backend picks the executor (the depot shown)
          jobData.serviceProviderId = BigInt(selectedCompanyId);
          if (selectedReceiver) {
            jobData.serviceReceiverId = BigInt(selectedReceiver.id);
          }
        }

        let createdJob: ConnectProto.Job;
        try {
          createdJob = await createJob(jobData).unwrap();
        } catch (error) {
          const apiError = error as ApiError;
          const routeGroupRefused =
            apiError.code === ErrorCode.ERROR_CODE_INVALID_FIELD_VALUE &&
            apiError.args?.field === "pickup_location_group";
          if (!routeGroupRefused) throw error;
          setRouteGroupError(describeError(apiError));
          return;
        }
        if (createdJob?.id) createdNumbers.push(`Z${createdJob.id}`);
        if (zone.length > 0) {
          depot =
            createdJob.serviceProvider?.name ??
            zone.find((org) => String(org.id) === String(createdJob.serviceProviderId))?.name ??
            null;
        }
        created += 1;

        if (instructionFiles.length > 0) {
          // The provider comes off the created job rather than being rebuilt from form state:
          // AttachJobInstructions is provider-side only, and the server checks the id against
          // the job it owns.
          await attachJobInstructions({
            jobId: createdJob.id,
            serviceProviderId: createdJob.serviceProviderId,
            files: instructionFiles,
          }).unwrap();
        }
      }
      resetForm();
      setCreatedJobNumbers(createdNumbers);
      setCreatedJobDepot(depot);
      setCreatedJobsCount(created);
      setIsCreateSuccess(true);
      return;
    }

    onClose();
  };

  // A new order on a day the schedule already covers is likely a duplicate: ask first.
  const confirmScheduledDay = () => {
    const modalId = openModal({
      size: "sm",
      title: t(Namespace.Orders, "create.scheduledOnDayTitle"),
      body: <span>{t(Namespace.Orders, "create.scheduledOnDayBody")}</span>,
      footer: (
        <div className="flex justify-end gap-2 w-full">
          <Button variant="outline" onClick={() => closeModal(modalId)}>
            {t(Namespace.Common, "cancel")}
          </Button>
          <Button
            onClick={() => {
              closeModal(modalId);
              submit();
            }}
          >
            {t(Namespace.Orders, "create.scheduledOnDayConfirm")}
          </Button>
        </div>
      ),
    });
  };

  const handleCancelJob = () => {
    if (liveJob) cancelJobs([liveJob], (allCancelled) => allCancelled && onClose());
  };

  return (
    <>
      <div
        data-testid="order-form"
        className="w-full flex flex-1 min-h-0 flex-col overflow-y-auto hidden-scrollbar"
      >
        <div className="flex flex-1 flex-col gap-8 p-4 md:flex-row md:pb-4">
          <div className="flex min-w-0 flex-1 flex-col gap-4 xl:min-w-[480px]">
            {/* Not around the author block, whose chat history stays open to a read-only job */}
            <fieldset disabled={isLocked} className="contents">
              <OrderFormRow
                label={t(Namespace.Orders, "create.customer")}
                htmlFor="create-order-receiver-select-button"
              >
                <SearchablePopover
                  open={isReceiverPopoverOpen}
                  onOpenChange={setIsReceiverPopoverOpen}
                  searchValue={receiverSearchQuery}
                  onSearchChange={setReceiverSearchQuery}
                  inputId="create-order-receiver-search-input"
                  inputTestId="order-form-customer-search"
                  placeholder={t(Namespace.Orders, "create.customerSearchPlaceholder")}
                  contentProps={{ align: "start", matchTriggerWidth: true }}
                  trigger={
                    <Button
                      id="create-order-receiver-select-button"
                      data-testid="order-form-customer"
                      variant="outline"
                      size="sm"
                      className={dropdownTriggerClass}
                      disabled={isEditMode || isLoadingReceivers}
                    >
                      <TriggerValue
                        icon={IcCompanies}
                        value={selectedReceiver?.name}
                        placeholder={t(Namespace.Orders, "create.customerPlaceholder")}
                      />
                      {selectedReceiverBin && (
                        <span className="min-w-0 truncate text-xs font-semibold text-text-subtle">
                          {t(Namespace.Orders, "create.customerBin", {
                            bin: selectedReceiverBin,
                          })}
                        </span>
                      )}
                      <ChevronDown className={chevronClass} />
                    </Button>
                  }
                >
                  {selectedReceiver && (
                    <button
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm text-red-600 border-b"
                      onClick={handleReceiverClear}
                    >
                      {t(Namespace.Orders, "create.resetSelection")}
                    </button>
                  )}
                  {filteredReceivers.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {t(Namespace.Orders, "create.noResults")}
                    </div>
                  ) : (
                    filteredReceivers.map((rec) => (
                      <button
                        key={rec.id}
                        data-testid="order-form-option"
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                        onClick={() => handleReceiverPick(rec)}
                      >
                        {/* Name on the left, BIN muted on the right — the search matches either. */}
                        <div className="flex min-w-0 items-center justify-between gap-3">
                          <span className="truncate font-medium">{rec.name}</span>
                          {rec.bin?.trim() && (
                            <span className="shrink-0 text-text-secondary">{rec.bin.trim()}</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </SearchablePopover>
              </OrderFormRow>

              <OrderFormRow
                label={t(Namespace.Orders, "create.object")}
                htmlFor="create-order-location-select-button"
              >
                <SearchablePopover
                  open={isLocationPopoverOpen}
                  onOpenChange={setIsLocationPopoverOpen}
                  searchValue={locationSearchQuery}
                  onSearchChange={setLocationSearchQuery}
                  inputId="create-order-location-search-input"
                  placeholder={t(Namespace.Orders, "create.searchPlaceholder")}
                  contentProps={{ align: "start", matchTriggerWidth: true }}
                  trigger={
                    <Button
                      id="create-order-location-select-button"
                      data-testid="order-form-location"
                      variant="outline"
                      size="sm"
                      className={`${dropdownTriggerClass} ${showNoContractError ? dropdownErrorClass : ""}`}
                      disabled={isEditMode || isLoadingLocations}
                    >
                      <TriggerValue
                        icon={IcLocation}
                        value={selectedLocation?.name}
                        placeholder={t(Namespace.Orders, "create.objectPlaceholder")}
                      />
                      <ChevronDown className={chevronClass} />
                    </Button>
                  }
                >
                  {selectedLocation && (
                    <button
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm text-red-600 border-b"
                      onClick={handleLocationClear}
                    >
                      {t(Namespace.Orders, "create.resetSelection")}
                    </button>
                  )}
                  {isLoadingLocations ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {t(Namespace.Orders, "create.loading")}
                    </div>
                  ) : filteredLocations.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {locationSearchQuery
                        ? t(Namespace.Orders, "create.noResults")
                        : t(Namespace.Orders, "create.noLocations")}
                    </div>
                  ) : (
                    filteredLocations.map((location) => (
                      <button
                        key={location.id}
                        data-testid="order-form-option"
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                        onClick={() =>
                          handleLocationSelect({
                            id: location.id.toString(),
                            name: location.name,
                            tariff: location.tariff,
                            type: location.type,
                            availabilitySchedule: location.availability_schedule,
                            ownerOrganizationId: location.ownerOrganizationId,
                          })
                        }
                        disabled={isEditMode}
                      >
                        <div className="font-medium">{location.name}</div>
                      </button>
                    ))
                  )}
                </SearchablePopover>
              </OrderFormRow>

              <OrderFormRow
                label={t(Namespace.Orders, "create.containerGroup")}
                htmlFor="create-order-container-group-select-button"
              >
                {/* The count wraps under the group once the group's picker would get narrower than 200px */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="min-w-[200px] flex-1">
                    <SearchablePopover
                      open={isContainerGroupPopoverOpen}
                      onOpenChange={setIsContainerGroupPopoverOpen}
                      searchValue={containerGroupSearchQuery}
                      onSearchChange={setContainerGroupSearchQuery}
                      inputId="create-order-container-group-search-input"
                      placeholder={t(Namespace.Orders, "create.containerGroupSearchPlaceholder")}
                      // Wider than the trigger, so a row fits both the expired-contract badge and
                      // the group's name; max-w keeps the panel on screen.
                      contentClassName="min-w-[24rem] max-w-[calc(100vw-2rem)]"
                      contentProps={{ align: "start", matchTriggerWidth: true }}
                      trigger={
                        <Button
                          id="create-order-container-group-select-button"
                          data-testid="order-form-container-group"
                          variant="outline"
                          size="sm"
                          className={dropdownTriggerClass}
                        >
                          <TriggerValue
                            value={selectedContainerGroupsLabel}
                            placeholder={t(Namespace.Orders, "create.containerGroupPlaceholder")}
                          />
                          <ChevronDown className={chevronClass} />
                        </Button>
                      }
                    >
                      {filteredContainerGroups.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {t(Namespace.Orders, "create.noResults")}
                        </div>
                      ) : (
                        filteredContainerGroups.map((cg) => {
                          const isSelected = selectedContainerGroupIds.includes(cg.id.toString());
                          return (
                            <button
                              key={cg.id.toString()}
                              data-testid="order-form-option"
                              className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm flex items-center gap-2${
                                isSelected ? " bg-gray-50" : ""
                              }`}
                              onClick={() => handleContainerGroupToggle(cg)}
                            >
                              {isContractExpired(cg) && (
                                <span className="flex-shrink-0 whitespace-nowrap rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium leading-none text-red-600">
                                  {t(Namespace.Orders, "create.contractExpired")}
                                </span>
                              )}
                              <span className="font-medium">{formatContainerGroupLabel(cg)}</span>
                            </button>
                          );
                        })
                      )}
                    </SearchablePopover>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <label
                      htmlFor="create-order-containers-input"
                      className="text-sm text-text-body"
                    >
                      {t(Namespace.Orders, "create.containersCount")}
                    </label>
                    <CountStepper
                      id="create-order-containers-input"
                      testId="order-form-containers"
                      value={containersInput}
                      onChange={setContainersInput}
                      invalid={showContainersError}
                    />
                  </div>
                </div>
                {scheduleLines.length > 0 && (
                  <p className="mt-1 text-xs text-icon-secondary">
                    {t(Namespace.Orders, "create.pickupSchedule")}: {scheduleLines.join("; ")}
                  </p>
                )}
                {expiredContractNote}
              </OrderFormRow>

              {contractLabel && (
                <OrderFormRow
                  label={t(Namespace.Orders, "create.contract")}
                  htmlFor="create-order-contract-input"
                >
                  <Input
                    id="create-order-contract-input"
                    data-testid="order-form-contract"
                    className={`!p-2 !pl-4 max-h-8 !border-border-primary ${DISABLED_INPUT_CLASS}`}
                    value={contractLabel}
                    title={contractLabel}
                    disabled
                  />
                </OrderFormRow>
              )}

              <OrderFormRow
                label={t(Namespace.Orders, "create.routeGroup")}
                htmlFor="create-order-route-group-select-button"
              >
                <SearchablePopover
                  open={isRouteGroupPopoverOpen}
                  onOpenChange={setIsRouteGroupPopoverOpen}
                  searchValue={routeGroupSearchQuery}
                  onSearchChange={setRouteGroupSearchQuery}
                  inputId="create-order-route-group-search-input"
                  placeholder={t(Namespace.Orders, "create.routeGroupSearchPlaceholder")}
                  contentProps={{ align: "start", matchTriggerWidth: true }}
                  trigger={
                    <Button
                      id="create-order-route-group-select-button"
                      data-testid="order-form-route-group"
                      variant="outline"
                      size="sm"
                      className={dropdownTriggerClass}
                      disabled={isEditMode || isLoadingRouteGroups || zoned}
                    >
                      <TriggerValue
                        icon={IcGroupOfLocations}
                        value={routeGroupName}
                        placeholder={t(
                          Namespace.Orders,
                          zoned ? "create.routeGroupByZone" : "create.routeGroupPlaceholder",
                        )}
                      />
                      <ChevronDown className={chevronClass} />
                    </Button>
                  }
                >
                  {selectedRouteGroup && (
                    <button
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm text-red-600 border-b"
                      onClick={handleRouteGroupClear}
                    >
                      {t(Namespace.Orders, "create.resetSelection")}
                    </button>
                  )}
                  {isFetchingRouteGroups ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {t(Namespace.Orders, "create.loading")}
                    </div>
                  ) : filteredRouteGroups.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {routeGroupSearchQuery
                        ? t(Namespace.Orders, "create.noResults")
                        : t(Namespace.Orders, "create.noRouteGroups")}
                    </div>
                  ) : (
                    filteredRouteGroups.map((group) => (
                      <button
                        key={group.id}
                        data-testid="order-form-option"
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                        onClick={() => handleRouteGroupSelect(group)}
                      >
                        <div className="font-medium">{group.name}</div>
                      </button>
                    ))
                  )}
                </SearchablePopover>
                {routeGroupError && (
                  <FieldError testId="order-form-route-group-error">{routeGroupError}</FieldError>
                )}
              </OrderFormRow>

              <OrderFormRow
                label={t(Namespace.Orders, "create.jobType")}
                htmlFor="create-order-type-select-button"
              >
                <Popover open={isJobTypePopoverOpen} onOpenChange={setIsJobTypePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id="create-order-type-select-button"
                      data-testid="order-form-job-type"
                      variant="outline"
                      size="sm"
                      className={`${dropdownTriggerClass} ${showNoTariffError ? dropdownErrorClass : ""}`}
                      disabled={!selectedContainerGroupId}
                    >
                      <TriggerValue
                        icon={IcTask}
                        value={jobTypes.find((jt) => jt.value === effectiveJobTypeNum)?.label}
                        placeholder={t(Namespace.Orders, "create.jobTypePlaceholder")}
                      />
                      <ChevronDown className={chevronClass} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="!p-0" align="start" matchTriggerWidth>
                    <div className="max-h-60 overflow-auto">
                      {/* Options come sorted by category: each category opens with its heading */}
                      {jobTypeOptions.map((typeOption, index) => {
                        const categorised = jobTypeRank.get(typeOption.value);
                        const category = categorised?.category;
                        const previousCategory =
                          index > 0
                            ? jobTypeRank.get(jobTypeOptions[index - 1].value)?.category
                            : undefined;
                        return (
                          <Fragment key={typeOption.value}>
                            {category && category !== previousCategory && (
                              <div className="px-4 pb-1 pt-2 text-xs font-semibold text-text-secondary">
                                {category}
                              </div>
                            )}
                            <button
                              data-testid="order-form-option"
                              className={`flex w-full items-center py-2 pr-4 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${category ? "pl-8" : "pl-4"}`}
                              onClick={() => handleJobTypeSelect(typeOption)}
                            >
                              <span className="min-w-0 flex-1 truncate font-medium">
                                {categorised?.label ?? typeOption.label}
                              </span>
                            </button>
                          </Fragment>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </OrderFormRow>

              <OrderFormRow
                label={t(Namespace.Orders, "create.tariffPerCube")}
                htmlFor="create-order-tariff-per-cube-input"
              >
                <Input
                  id="create-order-tariff-per-cube-input"
                  data-testid="order-form-tariff"
                  className={`!p-2 !pl-4 max-h-8 !pr-16 !border-border-primary ${DISABLED_INPUT_CLASS}`}
                  value={contractTariffForJobType ?? selectedLocation?.tariff ?? "0"}
                  disabled
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 !text-sm text-slate-400 pointer-events-none">
                  {t(Namespace.Orders, "create.tenge")}
                </span>
              </OrderFormRow>
            </fieldset>

            {/* Outside the read-only fieldset: a closed job's price can still be corrected */}
            <OrderFormRow
              label={t(Namespace.Orders, "create.finalCost")}
              htmlFor="create-order-final-cost-input"
            >
              <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-8">
                <div className="relative w-full max-w-[160px] lg:flex-shrink-0">
                  <Input
                    id="create-order-final-cost-input"
                    data-testid="order-form-final-cost"
                    className={`!p-2 !pl-4 !pr-16 max-h-8 ${isFinalCostDisabled ? DISABLED_INPUT_CLASS : ""} ${isFinalCostInvalid ? dropdownErrorClass : ""}`}
                    inputMode="numeric"
                    aria-invalid={isFinalCostInvalid}
                    value={finalCostInput}
                    onChange={(e) => setFinalCostInput(e.target.value)}
                    disabled={isFinalCostDisabled}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 !text-sm text-gray-500 pointer-events-none">
                    {t(Namespace.Orders, "create.tenge")}
                  </span>
                </div>
                <label className="flex max-w-full items-center gap-3 text-sm text-text-body">
                  <Switch
                    id="create-order-custom-cost-switch"
                    data-testid="order-form-custom-cost"
                    disabled={isWriteBlocked}
                    checked={isCustomCost}
                    onCheckedChange={setIsCustomCost}
                  />
                  {t(Namespace.Orders, "create.customCost")}
                </label>
              </div>
            </OrderFormRow>
            {isFinalCostInvalid && (
              <FieldError testId="order-form-final-cost-error">
                {t(Namespace.Orders, "create.finalCostInvalid")}
              </FieldError>
            )}

            <fieldset disabled={isLocked} className="contents">
              {/* Fixed on creation; the card can hand the job to any organization the user reaches */}
              {depotName && (
                <OrderFormRow
                  label={t(Namespace.Orders, "create.depot")}
                  htmlFor="create-order-depot-button"
                >
                  <Popover open={isDepotPopoverOpen} onOpenChange={setIsDepotPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="create-order-depot-button"
                        data-testid="order-form-depot"
                        variant="outline"
                        size="sm"
                        className={dropdownTriggerClass}
                        disabled={!canChangeDepot}
                      >
                        <TriggerValue icon={IcGarage} value={depotName} placeholder="" />
                        {!isEditMode && (
                          <span className="shrink-0 text-xs font-medium text-text-secondary">
                            {t(Namespace.Orders, "create.depotPinned")}
                          </span>
                        )}
                        <ChevronDown className={chevronClass} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="!p-0" align="start" matchTriggerWidth>
                      <div className="max-h-60 overflow-auto">
                        {depotOptions.map((org) => (
                          <button
                            key={String(org.id)}
                            data-testid="order-form-option"
                            className="w-full px-4 py-2 text-left text-sm font-medium hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            onClick={() => {
                              setPickedDepotId(org.id);
                              setDepotError(null);
                              setIsDepotPopoverOpen(false);
                            }}
                          >
                            {org.name}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {depotError && (
                    <FieldError testId="order-form-depot-error">{depotError}</FieldError>
                  )}
                </OrderFormRow>
              )}

              {/* A card edits its one job, so there is nothing to duplicate */}
              {!isEditMode && (
                <OrderFormRow
                  label={t(Namespace.Orders, "create.duplicate")}
                  htmlFor="create-order-duplicate-switch"
                >
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                    <label className="flex items-center gap-3 text-sm text-text-body">
                      {t(Namespace.Orders, "create.duplicateToggle")}
                      <Switch
                        id="create-order-duplicate-switch"
                        data-testid="order-form-duplicate"
                        checked={isDuplicate}
                        // Off creates one job, so the count goes back to it
                        onCheckedChange={(checked) => {
                          setIsDuplicate(checked);
                          if (!checked) setCountInput("1");
                        }}
                      />
                    </label>
                    <CountStepper
                      id="create-order-count-input"
                      testId="order-form-count"
                      value={countInput}
                      onChange={setCountInput}
                      invalid={showCountError}
                      disabled={!isDuplicate}
                    />
                  </div>
                </OrderFormRow>
              )}
              {showCountError && (
                <FieldError testId="order-form-count-error">
                  {t(Namespace.Orders, "create.countInvalid")}
                </FieldError>
              )}
            </fieldset>
            {/* Only an organization with the AI dispatcher has a job to hide from it */}
            {isAiFeatureEnabled && (
              <div className="flex h-6 items-center gap-4">
                <label
                  htmlFor="hide-from-ai-switch"
                  className="w-[200px] text-sm font-medium leading-4 tracking-[-0.2px] text-text-body"
                >
                  {t(Namespace.Orders, "create.hideFromAiDispatcherLabel")}
                </label>
                <Switch
                  id="hide-from-ai-switch"
                  data-testid="order-form-hide-ai"
                  // Outside the read-only fieldset, so disabled with it by hand
                  disabled={isLocked}
                  checked={hideFromAi}
                  onCheckedChange={setHideFromAi}
                />
              </div>
            )}
            {authorBlock}
          </div>

          {/* The design's 440px column; on a narrower screen it gives way before the form does */}
          {mapPoint && (
            <div
              data-testid="order-form-map"
              className="w-[440px] min-w-[280px] overflow-hidden rounded-lg border border-slate-200"
            >
              <LocationMap
                center={mapPoint}
                pin={mapPoint}
                geofences={locationModalData?.geofence ?? NO_GEOFENCES}
              />
            </div>
          )}

          <div className="flex w-full flex-col gap-4 md:max-w-[23.5rem] md:flex-shrink-0">
            <fieldset disabled={isLocked} className="contents">
              <PickupScheduleBlock
                value={pickupWindow}
                onChange={(window) => {
                  windowTouched.current = true;
                  setTwWindow(window);
                }}
                availability={
                  isClockTime(selectedLocation?.startTime) && isClockTime(selectedLocation?.endTime)
                    ? { from: selectedLocation.startTime, to: selectedLocation.endTime }
                    : undefined
                }
                datePickerSide={isMobileOrTablet ? "top" : "left"}
              />

              <InstructionPhotosField
                drafts={instructionPhotos}
                onDraftsChange={setInstructionPhotos}
                attached={attachedInstructions}
                onRemoveAttached={handleRemoveAttachedInstruction}
                disabled={isLoading || isLocked || isClosed}
              />

              <div className="flex flex-1 flex-col gap-1">
                <Label
                  htmlFor="create-order-comment-textarea"
                  className="flex min-h-8 items-center !text-sm !font-medium !leading-4 tracking-[-0.2px] text-text-body"
                >
                  {t(Namespace.Orders, "create.customerComment")}
                </Label>
                <TextArea
                  id="create-order-comment-textarea"
                  data-testid="order-form-comment"
                  size="2"
                  // Fills the column to its bottom; no `rows`, whose height pushed the column into scrolling
                  className="flex-1 resize-none !rounded-lg !border-border-primary !p-2 !text-sm !leading-5 tracking-[-0.01em] text-text-body placeholder:!text-slate-400"
                  // The example is for a new job; an existing one's empty comment stays empty
                  placeholder={
                    isEditMode ? undefined : t(Namespace.Orders, "create.commentPlaceholder")
                  }
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>
      {isCreateSuccess && (
        <div className="my-2 mx-4 relative bg-green-50 flex flex-col gap-1 border border-green-200 rounded-lg px-3 py-2 pr-8">
          <p className="text-green-700 text-sm">
            {createdJobsCount > 1
              ? t(Namespace.Orders, "create.jobsCreatedSuccess" as any, {
                  count: createdJobsCount,
                })
              : t(Namespace.Orders, "create.jobCreatedSuccess" as any)}
            {createdJobNumbers.length > 0 && (
              <span className="font-medium"> {createdJobNumbers.join(", ")}</span>
            )}
          </p>
          {createdJobDepot && (
            <p className="text-green-700 text-sm">
              {t(Namespace.Orders, "create.responsibleDepot", { name: createdJobDepot })}
            </p>
          )}
          <button
            onClick={() => setIsCreateSuccess(false)}
            className="absolute right-2 top-1.5 text-green-600 hover:text-green-800 transition-colors"
            aria-label={t(Namespace.Orders, "create.close" as any)}
          >
            <IcCancel className="h-5 w-5" />
          </button>
        </div>
      )}
      {isErrorVisible && (
        <div className="my-2 mx-4 relative bg-red-50 flex flex-col gap-1 border border-red-200 rounded-lg px-3 py-2 pr-8">
          {showNoLocationsError && (
            <p className="text-red-700 text-sm">
              {t(Namespace.Orders, "create.noLocationsError" as any)}
            </p>
          )}
          {showNoContractError && (
            <>
              <p className="text-red-700 text-sm">
                {t(Namespace.Orders, "create.noContractOnLocation")}
              </p>
              <button
                type="button"
                className="text-sm text-brand-blue font-semibold text-left"
                disabled={!selectedLocation}
                onClick={() => {
                  if (selectedLocation) setIsLocationModalOpen(true);
                }}
              >
                {t(Namespace.Orders, "create.openLocationCard" as any)}
                <ArrowRight className="inline-block w-4 h-4 ml-1" />
              </button>
            </>
          )}
          {showNoTariffError && (
            <>
              <p className="text-red-700 text-sm">
                {t(Namespace.Orders, "create.noTariffForJobType")}
              </p>
              <div className="flex flex-col gap-1 items-start">
                <button
                  type="button"
                  className="text-sm text-brand-blue font-semibold text-left"
                  disabled={!selectedReceiver}
                  onClick={() => {
                    const rec = receivers.find((r) => r.id.toString() === selectedReceiver?.id);
                    if (rec) openCustomerCard(rec);
                  }}
                >
                  {t(Namespace.Orders, "create.openCustomerCard" as any)}
                  <ArrowRight className="inline-block w-4 h-4 ml-1" />
                </button>
                <button
                  type="button"
                  className="text-sm text-brand-blue font-semibold text-left"
                  disabled={!selectedLocation}
                  onClick={() => {
                    if (selectedLocation) setIsLocationModalOpen(true);
                  }}
                >
                  {t(Namespace.Orders, "create.openLocationCard" as any)}
                  <ArrowRight className="inline-block w-4 h-4 ml-1" />
                </button>
              </div>
            </>
          )}
          <button
            onClick={() => {
              setDismissedErrorSignature(errorSignature);
              setSelectedJobType(null);
              setSelectedLocation(null);
              setPickupLocationId("");
              setSelectedContainerTypeId(null);
              setSelectedContainerGroupId(null);
              setSelectedReceiver(null);
              setFinalCost(0);
              isLocationPinnedRef.current = false;
              receiverManuallyClearedRef.current = false;
              locationManuallyClearedRef.current = false;
            }}
            className="absolute right-2 top-1.5 text-red-600 hover:text-red-800 transition-colors"
            aria-label={t(Namespace.Orders, "create.close" as any)}
          >
            <IcCancel className="h-5 w-5" />
          </button>
        </div>
      )}
      {!isForeign && (
        <ModalFooter className="!px-4">
          {isEditMode && isJobCancellable(liveJob?.status) && (
            <Button
              id="job-card-cancel-job-button"
              data-testid="job-card-cancel-job"
              variant="solidRed"
              className="mr-auto !h-8 !px-3 !font-normal"
              onClick={handleCancelJob}
              disabled={isLoading || isOwnUnknown}
            >
              {t(Namespace.Orders, "actions.cancelOrder")}
            </Button>
          )}
          {isOwnUnknown && (
            <span className="text-sm text-text-secondary">
              {t(Namespace.Orders, "create.checkingAccess")}
            </span>
          )}
          {/* A contract or tariff that lapsed since a job closed must not lock a correction out */}
          <Button
            id="create-order-submit-button"
            data-testid="order-form-submit"
            className="!h-8 !px-3 !font-normal"
            onClick={scheduledOnPickupDay ? confirmScheduledDay : submit}
            disabled={
              isLoading ||
              isOwnUnknown ||
              schedulesPending ||
              !pickupLocationId ||
              // submit falls back to a closed job's own type and container type
              (!isClosed && (!selectedContainerTypeId || !selectedJobType)) ||
              (!isClosed && (hasNoContractOnLocation || hasTariffForCurrentJobType === false)) ||
              isFinalCostInvalid ||
              showCountError ||
              showContainersError
            }
          >
            {isEditMode ? t(Namespace.Common, "save") : t(Namespace.Orders, "create.submit")}
          </Button>
        </ModalFooter>
      )}
      <MemoryRouter>
        <PickupLocationModal
          location={locationModalData}
          open={isLocationModalOpen}
          onOpenChange={setIsLocationModalOpen}
          hideTrigger
          // Not in the loaded list (yet): read-only, never an empty editable form.
          readOnly={
            !fullLocation ||
            isOwnSiteForWrite(fullLocation.ownerOrganizationId, fullLocation.serviceProviderId) !==
              true
          }
        />
      </MemoryRouter>
    </>
  );
};
